import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';

/**
 * Permission levels in order of increasing access.
 * none < read < own < full
 */
const PERMISSION_LEVELS = ['none', 'read', 'own', 'full'] as const;
type PermissionLevel = (typeof PERMISSION_LEVELS)[number];

/**
 * Permission subjects that can be checked.
 */
type PermissionSubject =
  | 'contact'
  | 'event'
  | 'expense'
  | 'bankAccount'
  | 'moneyTransfer'
  | 'project'
  | 'checklist'
  | 'instruction'
  | 'practicalInformation'
  | 'assetStorage'
  | 'entityExport'
  | 'financeAdmin'
  | 'task'
  | 'dashboard'
  | 'cartography'
  | 'deductibleCharges';

/**
 * Hook to check if the current user has at least the required permission level
 * for a given subject on the current asset.
 *
 * @param subject - The permission subject to check
 * @param minLevel - The minimum required permission level (default: 'read')
 * @returns Object with hasPermission boolean, isLoading state, and owner info
 *
 * @example
 * const { hasPermission, isLoading, ownerName } = useHasPermission('contact', 'read');
 * if (!hasPermission) return <AccessDenied ownerName={ownerName} />;
 */
export const useHasPermission = (
  subject: PermissionSubject,
  minLevel: PermissionLevel = 'read'
) => {
  const { data: asset, isLoading } = useFetchAssetDetails();

  // Owner always has full permissions
  if (asset?.isOwner) {
    return {
      hasPermission: true,
      isLoading,
      ownerName: undefined,
      permissionLevel: 'full' as PermissionLevel,
    };
  }

  const permissions = asset?.currentUserPermissions as Record<string, string> | undefined;
  const currentLevel = (permissions?.[subject] ?? 'none') as PermissionLevel;

  const currentLevelIndex = PERMISSION_LEVELS.indexOf(currentLevel);
  const minLevelIndex = PERMISSION_LEVELS.indexOf(minLevel);

  const hasPermission = currentLevelIndex >= minLevelIndex;
  const ownerName = asset?.owner
    ? [asset.owner.firstName, asset.owner.lastName].filter(Boolean).join(' ')
    : undefined;

  return {
    hasPermission,
    isLoading,
    ownerName,
    permissionLevel: currentLevel,
  };
};

/**
 * Hook to check if user can create new items for a subject.
 * Requires 'own' level minimum.
 */
export const useCanCreate = (subject: PermissionSubject) => {
  return useHasPermission(subject, 'own');
};

/**
 * Hook to check if user can view items for a subject.
 * Requires 'read' level minimum.
 */
export const useCanRead = (subject: PermissionSubject) => {
  return useHasPermission(subject, 'read');
};

/**
 * Hook to check if user can edit/delete a specific item.
 * - 'full' level: can edit/delete any item
 * - 'own' level: can only edit/delete items they created
 * - 'read' or 'none' level: cannot edit/delete
 *
 * @param subject - The permission subject
 * @param itemCreatedById - The ID of the user who created the item (optional for 'full' level check)
 * @returns Object with canEdit and canDelete booleans
 */
export const useItemPermissions = (subject: PermissionSubject, itemCreatedById?: string) => {
  const { data: user } = useFetchUser();
  const { permissionLevel, isLoading, ownerName } = useHasPermission(subject, 'read');

  const currentUserId = user?.id;

  // If user has 'full' permission, they can edit/delete anything
  if (permissionLevel === 'full') {
    return { canEdit: true, canDelete: true, isLoading, ownerName };
  }

  // If user has 'own' permission, they can only edit/delete their own items
  if (permissionLevel === 'own') {
    const isOwnItem = itemCreatedById && currentUserId && itemCreatedById === currentUserId;
    return { canEdit: isOwnItem, canDelete: isOwnItem, isLoading, ownerName };
  }

  // 'read' or 'none' level cannot edit/delete
  return { canEdit: false, canDelete: false, isLoading, ownerName };
};
